import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Injectable, Inject, Optional } from '@angular/core';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CryptoService } from '../services/crypto.service';
import { SECURITY_CONFIG, SecurityConfig } from '../models/security-config.interface';

/** Encrypts requests: POST/PUT/PATCH body + headers, GET generates keys for response encryption */
@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
  constructor(
    private cryptoService: CryptoService,
    @Optional() @Inject(SECURITY_CONFIG) private config: SecurityConfig | null
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const cryptoService = this.cryptoService;
    const config = this.config;

    if (config?.enableEncryption === false) return next.handle(req);

    const encryptPatterns = config?.encryptDecryptForEndpoints || ['*'];
    const skipPatterns = config?.skipEncryptDecryptForEndpoints;
    const shouldEncrypt = cryptoService.matchesPattern(req.url, encryptPatterns, skipPatterns);

    if (!shouldEncrypt) return next.handle(req);
    if (config?.publicKeyEndpoint && req.url === config.publicKeyEndpoint) return next.handle(req);

    // GET: Generate encrypted headers for response encryption
    if (req.method === 'GET') {
      return from(cryptoService.generateEncryptedHeaders()).pipe(
        switchMap(headers => {
          if (!headers) return next.handle(req);
          const encryptedReq = req.clone({
            setHeaders: {
              'X-Request-Context': headers.encryptedAesKey,
              'X-Client-Ref': headers.encryptedIV
            }
          });
          return next.handle(encryptedReq);
        })
      );
    }

    if (!req.body || typeof req.body !== 'object') return next.handle(req);
    if (req.method === 'DELETE' || req.method === 'HEAD' || req.method === 'OPTIONS') return next.handle(req);

    // POST/PUT/PATCH: Encrypt body
    if (config?.enableDebugLogging) {
      console.log(`[Encryption] ${req.method} request body before encryption:`, req.body);
    }

    return from(cryptoService.createEncryptedEnvelope(req.body as Record<string, unknown>)).pipe(
      switchMap(encryptedBody => {
        const envelope = encryptedBody as Record<string, unknown>;
        const encryptedReq = req.clone({
          body: envelope['d'],
          setHeaders: {
            'Content-Type': 'application/json',
            'X-Request-Context': envelope['k'] as string,
            'X-Client-Ref': envelope['s'] as string
          }
        });
        return next.handle(encryptedReq);
      })
    );
  }
}
